// src/components/ClassReports.js — reports by class, event P&L, audit trail, closing date
// [marker: books-pro v1]
// <ProfitLossByClass />  <FunctionalExpenses />  <EventProfitLoss />  <AuditTrail />  <ClosingDateButton />
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { RangePicker, rangePreset, useBasis, BasisToggle } from './AccountReports'; // [marker: cash-basis v1]
import { CATEGORY_LABELS } from './ClassSelect';

const money = (n) => { const v = parseFloat(n || 0); return `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
const fmtDay = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const INCOME = ['income', 'other_income'];
const th = { textAlign: 'right', whiteSpace: 'nowrap', fontSize: '0.75rem' };
const cell = { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

function exportCsv(name, header, rows) {
  const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map(r => r.map(esc).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' })); a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

// ─── P&L by class ─────────────────────────────────────────────────────────────
export function ProfitLossByClass() {
  const [range, setRange] = useState(() => rangePreset('ytd'));
  const [rollup, setRollup] = useState(false);
  const [data, setData] = useState(null);
  const [basis, setBasis] = useBasis();
  const load = useCallback(() => {
    setData(null);
    api.get(`/classes/reports/pl?from=${range.from}&to=${range.to}&basis=${basis}${rollup ? '&rollup=1' : ''}`).then(r => setData(r.data)).catch(() => toast.error('Could not load the report.'));
  }, [range, rollup, basis]);
  useEffect(() => { load(); }, [load]);
  const cols = data ? [...data.classes.map(c => ({ key: String(c.id), label: c.name })), ...(data.hasUnclassed ? [{ key: 'none', label: 'Not classed' }] : [])] : [];
  const inc = (data?.accounts || []).filter(a => INCOME.includes(a.type));
  const exp = (data?.accounts || []).filter(a => !INCOME.includes(a.type));
  const sum = (list, k) => list.reduce((s, a) => s + (k ? (a.by[k] || 0) : a.total), 0);
  const Section = ({ title, list }) => (
    <>
      <tr><td colSpan={cols.length + 2} style={{ fontWeight: 700, background: 'var(--gray-50)' }}>{title}</td></tr>
      {list.map(a => (
        <tr key={a.account_id}><td style={{ paddingLeft: 20 }}>{a.name}</td>
          {cols.map(c => <td key={c.key} style={cell}>{a.by[c.key] ? money(a.by[c.key]) : ''}</td>)}
          <td style={{ ...cell, fontWeight: 600 }}>{money(a.total)}</td></tr>
      ))}
      <tr style={{ fontWeight: 700 }}><td>Total {title}</td>{cols.map(c => <td key={c.key} style={cell}>{money(sum(list, c.key))}</td>)}<td style={cell}>{money(sum(list))}</td></tr>
    </>
  );
  const csv = () => exportCsv(`pl-by-class-${range.from}-${range.to}.csv`, ['Account', ...cols.map(c => c.label), 'Total'],
    (data?.accounts || []).map(a => [a.name, ...cols.map(c => a.by[c.key] || 0), a.total]));
  return (
    <div>
      <div className="card" style={{ padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <span style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <BasisToggle value={basis} onChange={setBasis} />
          <RangePicker value={range} onChange={setRange} />
          <label style={{ fontSize: '0.85rem', display: 'flex', gap: 5, alignItems: 'center' }}><input type="checkbox" checked={rollup} onChange={e => setRollup(e.target.checked)} /> Combine sub-classes (e.g. all events together)</label>
        </span>
        {data && <button className="btn btn-ghost btn-sm" onClick={csv}>⬇ Export</button>}
      </div>
      {!data ? <div className="spinner" /> : !data.accounts.length ? <div className="empty-state"><h3>No income or expenses in these dates.</h3></div> : (
        <div className="card"><div className="table-wrap" style={{ border: 'none', overflowX: 'auto' }}>
          <table>
            <thead><tr><th>Account</th>{cols.map(c => <th key={c.key} style={th}>{c.label}</th>)}<th style={th}>Total</th></tr></thead>
            <tbody>
              <Section title="Income" list={inc} />
              <Section title="Expenses" list={exp} />
              <tr style={{ fontWeight: 800, borderTop: '2px solid var(--gray-300)' }}><td>Net</td>
                {cols.map(c => { const v = sum(inc, c.key) - sum(exp, c.key); return <td key={c.key} style={{ ...cell, color: v < 0 ? 'var(--danger)' : undefined }}>{money(v)}</td>; })}
                <td style={cell}>{money(sum(inc) - sum(exp))}</td></tr>
            </tbody>
          </table>
        </div></div>
      )}
      <p className="form-hint" style={{ marginTop: 8 }}>“Not classed” is income or expense with no event or program picked. Pick classes on bills, checks and invoice lines to fill it in.</p>
    </div>
  );
}

// ─── Functional expenses (Form 990) ──────────────────────────────────────────
export function FunctionalExpenses() {
  const [range, setRange] = useState(() => rangePreset('last_year'));
  const [data, setData] = useState(null);
  const [basis, setBasis] = useBasis();
  useEffect(() => {
    setData(null);
    api.get(`/classes/reports/functional?from=${range.from}&to=${range.to}&basis=${basis}`).then(r => setData(r.data)).catch(() => toast.error('Could not load the report.'));
  }, [range, basis]);
  const keys = ['program', 'management', 'fundraising', 'none'];
  const label = { ...CATEGORY_LABELS, none: 'Not assigned' };
  const tot = (k) => (data?.accounts || []).reduce((s, a) => s + a[k], 0);
  return (
    <div>
      <div className="card" style={{ padding: '12px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <span style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}><BasisToggle value={basis} onChange={setBasis} /><RangePicker value={range} onChange={setRange} /></span>
        {data && <button className="btn btn-ghost btn-sm" onClick={() => exportCsv(`functional-expenses-${range.from}-${range.to}.csv`, ['Expense', ...keys.map(k => label[k]), 'Total'], data.accounts.map(a => [a.name, ...keys.map(k => a[k]), a.total]))}>⬇ Export</button>}
      </div>
      {!data ? <div className="spinner" /> : (
        <div className="card"><div className="table-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>Expense</th>{keys.map(k => <th key={k} style={th}>{label[k]}</th>)}<th style={th}>Total</th></tr></thead>
            <tbody>
              {data.accounts.map(a => <tr key={a.account_id}><td>{a.name}</td>{keys.map(k => <td key={k} style={{ ...cell, color: k === 'none' && a[k] ? '#b45309' : undefined }}>{a[k] ? money(a[k]) : ''}</td>)}<td style={{ ...cell, fontWeight: 600 }}>{money(a.total)}</td></tr>)}
              <tr style={{ fontWeight: 800 }}><td>Total</td>{keys.map(k => <td key={k} style={cell}>{money(tot(k))}</td>)}<td style={cell}>{money(tot('total'))}</td></tr>
            </tbody>
          </table>
        </div></div>
      )}
      <p className="form-hint" style={{ marginTop: 8 }}>Columns come from each class’s 990 category (Bookkeeping → Classes). “Not assigned” is spending with no class, or a class without a category.</p>
    </div>
  );
}

// ─── Event P&L (from the books) ──────────────────────────────────────────────
export function EventProfitLoss() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [data, setData] = useState(null);
  const [basis, setBasis] = useBasis();
  useEffect(() => { api.get('/events?limit=200&upcoming_only=false').then(r => setEvents(r.data.data || r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!eventId) return;
    setData(null);
    api.get(`/classes/reports/event/${eventId}?basis=${basis}`).then(r => setData(r.data)).catch(() => toast.error('Could not load.'));
  }, [eventId, basis]);
  return (
    <div>
      <div className="card" style={{ padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <BasisToggle value={basis} onChange={setBasis} />
        <select className="form-select" style={{ maxWidth: 460 }} value={eventId} onChange={e => setEventId(e.target.value)}>
          <option value="">Pick an event…</option>
          {events.map(e => <option key={e.id} value={e.id}>{e.name} — {fmtDay(e.event_date)}</option>)}
        </select>
      </div>
      {eventId && !data && <div className="spinner" />}
      {data && (
        <>
          <div className="stat-grid" style={{ marginBottom: 14 }}>
            <div className="stat-card"><div className="stat-label">Income</div><div className="stat-value" style={{ color: 'var(--success)' }}>{money(data.totalIncome)}</div></div>
            <div className="stat-card"><div className="stat-label">Costs</div><div className="stat-value" style={{ color: 'var(--danger)' }}>{money(data.totalExpenses)}</div></div>
            <div className="stat-card"><div className="stat-label">Net</div><div className="stat-value" style={{ color: data.net < 0 ? 'var(--danger)' : 'var(--success)' }}>{money(data.net)}</div></div>
          </div>
          {data.legacy && (
            <div style={{ fontSize: '0.85rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 14 }}>
              This event was before the books here began ({fmtDay(data.booksStart)}), so its money is in the QuickBooks history. From the ticket and sponsorship records:
              tickets <strong>{money(data.legacy.ticketRevenue)}</strong>, sponsorships <strong>{money(data.legacy.sponsorRevenue)}</strong>.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            {[['Income', data.income], ['Costs', data.expenses]].map(([t, list]) => (
              <div key={t} className="card"><div className="card-header"><span className="card-title">{t} by account</span></div>
                <div className="table-wrap" style={{ border: 'none' }}><table><tbody>
                  {list.length === 0 && <tr><td style={{ color: 'var(--gray-500)' }}>None tagged to this event yet.</td></tr>}
                  {list.map(x => <tr key={x.account}><td>{x.account}</td><td style={cell}>{money(x.amount)}</td></tr>)}
                </tbody></table></div></div>
            ))}
          </div>
          {data.lines.length > 0 && (
            <div className="card"><div className="card-header"><span className="card-title">Every transaction for this event</span></div>
              <div className="table-wrap" style={{ border: 'none' }}><table>
                <thead><tr><th>Date</th><th>Name</th><th>Memo</th><th>Account</th><th style={th}>Income</th><th style={th}>Cost</th></tr></thead>
                <tbody>{data.lines.map((l, i) => (
                  <tr key={i}><td style={{ whiteSpace: 'nowrap' }}>{fmtDay(l.date)}</td><td>{l.name}</td><td style={{ color: 'var(--gray-600)' }}>{l.memo}</td><td>{l.account_name}</td>
                    <td style={cell}>{l.kind_label === 'Income' ? money(l.amount) : ''}</td><td style={cell}>{l.kind_label === 'Expense' ? money(l.amount) : ''}</td></tr>
                ))}</tbody>
              </table></div></div>
          )}
          <p className="form-hint" style={{ marginTop: 8 }}>Ticket and sponsorship income is tagged to the event automatically. Tag costs by picking this event as the class on bills, checks and card charges.</p>
        </>
      )}
    </div>
  );
}

// ─── Audit trail ──────────────────────────────────────────────────────────────
export function AuditTrail() {
  const [range, setRange] = useState(() => rangePreset('this_month'));
  const [type, setType] = useState('');
  const [staffId, setStaffId] = useState('');
  const [hideAuto, setHideAuto] = useState(true);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    const p = new URLSearchParams({ from: range.from || '', to: range.to || '', type, staffId, hideAuto: hideAuto ? '1' : '' });
    api.get(`/books/audit?${p}`).then(r => setData(r.data)).catch(err => toast.error(err.response?.data?.error || 'Could not load.'));
  }, [range, type, staffId, hideAuto]);
  const rows = (data?.rows || []).filter(r => !q.trim() || `${r.document} ${r.who} ${r.type} ${r.changes.map(c => `${c.field} ${c.from} ${c.to}`).join(' ')}`.toLowerCase().includes(q.trim().toLowerCase()));
  const COLOR = { Added: 'var(--success)', Changed: '#2563eb', Deleted: 'var(--danger)', Voided: '#b45309' };
  return (
    <div>
      <div className="card" style={{ padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <RangePicker value={range} onChange={setRange} />
        <select className="form-select" style={{ width: 'auto' }} value={type} onChange={e => setType(e.target.value)}>
          <option value="">Everything</option><option value="invoices">Invoices &amp; their payments</option><option value="payments">Payments received &amp; deposits</option>
          <option value="bills">Bills &amp; bill payments</option><option value="credits">Customer credits</option><option value="register">Checks, charges &amp; register entries</option>
          <option value="setup">Accounts, classes &amp; closing date</option>
        </select>
        <select className="form-select" style={{ width: 'auto' }} value={staffId} onChange={e => setStaffId(e.target.value)}>
          <option value="">Everyone</option>
          {(data?.staff || []).map(s => <option key={s.id} value={s.id}>{[s.first_name, s.last_name].filter(Boolean).join(' ')}</option>)}
          <option value="auto">Automatic (system)</option>
        </select>
        <label style={{ fontSize: '0.85rem', display: 'flex', gap: 5, alignItems: 'center' }}><input type="checkbox" checked={hideAuto} onChange={e => setHideAuto(e.target.checked)} /> Hide automatic changes</label>
        <input className="form-input" style={{ width: 220, height: 34 }} placeholder="🔍 Search" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      {!data ? <div className="spinner" /> : rows.length === 0 ? <div className="empty-state"><h3>No changes in these dates.</h3></div> : (
        <div className="card"><div className="table-wrap" style={{ border: 'none' }}>
          <table>
            <thead><tr><th>When</th><th>Who</th><th>What</th><th>Action</th><th>Details</th></tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{new Date(r.at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                <td style={{ fontSize: '0.85rem', color: r.staff_id ? undefined : 'var(--gray-500)' }}>{r.who}</td>
                <td style={{ fontSize: '0.85rem' }}><div style={{ fontWeight: 600 }}>{r.document}</div><div style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>{r.type}</div></td>
                <td style={{ color: COLOR[r.action], fontWeight: 600, fontSize: '0.85rem' }}>{r.action}</td>
                <td style={{ fontSize: '0.8rem' }}>{r.changes.slice(0, 8).map((c, i) => (
                  <div key={i}><span style={{ color: 'var(--gray-500)' }}>{c.field}:</span> {r.action === 'Changed' || r.action === 'Voided'
                    ? <><span style={{ textDecoration: 'line-through', color: 'var(--gray-500)' }}>{c.from}</span> → <strong>{c.to}</strong></>
                    : <strong>{c.to || c.from}</strong>}</div>
                ))}</td>
              </tr>
            ))}</tbody>
          </table>
        </div></div>
      )}
      {data?.truncated && <p className="form-hint">Showing the latest 1,500 changes — narrow the dates to see older ones.</p>}
    </div>
  );
}

// ─── Closing date (header button on Bookkeeping) ──────────────────────────────
export function ClosingDateButton() {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const load = () => api.get('/books/closing-date').then(r => { setSt(r.data); setValue(r.data.closingDate || ''); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!st) return null;
  const save = async (v) => {
    try { await api.put('/books/closing-date', { closingDate: v || null }); toast.success(v ? `Books closed through ${fmtDay(v)}.` : 'Closing date removed.'); setOpen(false); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
  };
  return (
    <>
      <button className="btn btn-secondary btn-sm" onClick={() => setOpen(true)} title="Lock the books up to a date">
        {st.closingDate ? `🔒 Closed through ${fmtDay(st.closingDate)}` : '🔓 Closing date: none'}
      </button>
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)} style={{ zIndex: 1500 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header"><span className="modal-title">Closing Date</span><button className="btn btn-ghost btn-icon" onClick={() => setOpen(false)}>✕</button></div>
            <div className="modal-body">
              <p style={{ marginTop: 0, fontSize: '0.875rem' }}>
                Once a month or year is finished (reconciled, or taxes filed), close it: nothing dated on or before the closing date can be added,
                changed, voided or deleted. A super admin can still make a change after confirming a warning, and every change is in the Audit Trail.
              </p>
              <div className="form-group"><label className="form-label">Books closed through</label>
                <input className="form-input" type="date" value={value} disabled={!st.canEdit} onChange={e => setValue(e.target.value)} style={{ maxWidth: 200 }} /></div>
              {!st.canEdit && <p className="form-hint">Only a super admin can change the closing date.</p>}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <span>{st.canEdit && st.closingDate && <button className="btn btn-ghost" onClick={() => save(null)}>Remove closing date</button>}</span>
              <span style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
                {st.canEdit && <button className="btn btn-primary" disabled={!value} onClick={() => save(value)}>Save</button>}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
